import { useIntl } from "react-intl";
import { Info } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

interface NavItem {
  key: string;
  labelId: string;
  path: string;
  enabled: boolean;
  comingLabelId?: string;
}

const NAV_ITEMS: NavItem[] = [
  {
    key: "map",
    labelId: "LABEL_NAV_MAP",
    path: "/",
    enabled: true,
  },
  {
    key: "workers",
    labelId: "LABEL_NAV_WORKERS",
    path: "/workers",
    enabled: true,
  },
  // Phase 5 Plan 05 — PM admin tiles (D-121, D-128).
  {
    key: "queue-health",
    labelId: "LABEL_QUEUE_HEALTH_PAGE",
    path: "/admin/queue-health",
    enabled: true,
  },
  {
    key: "clock-skew",
    labelId: "LABEL_CLOCK_SKEW_TILE",
    path: "/admin/clock-skew",
    enabled: true,
  },
  // Phase 7 Plan 03 (D-164) — PM admin Outdated APK tile.
  {
    key: "outdated-apk",
    labelId: "LABEL_NAV_OUTDATED_APK",
    path: "/admin/outdated-apk",
    enabled: true,
  },
  // 2026-05-02 walkthrough P2 — the dedicated /inspections nav item was a
  // Phase 4 placeholder ("Coming in Phase 4") that never received a real
  // route. Phase 4 has been done since 2026-04-29 and per-asset inspection
  // viewing is surfaced by AssetSidePanel + the Phase 6 HistorySection. The
  // tooltip became misleading; removing the entry rather than re-aliasing
  // it to "Coming in v2" because no v2 ticket exists for a standalone
  // /inspections page.
  // Phase 6 Plan 02 (D-138 + RESEARCH Finding #4) — settings nav enabled.
  // LABEL_NAV_SETTINGS message text is already correct in both EN+AR (verified
  // live; Finding #4); only the enabled flag flips and comingLabelId is removed.
  {
    key: "settings",
    labelId: "LABEL_NAV_SETTINGS",
    path: "/settings",
    enabled: true,
  },
  // Phase 7 Plan 03 (D-150) — Exports page enabled. Was Phase 1 placeholder.
  {
    key: "exports",
    labelId: "LABEL_NAV_EXPORTS",
    path: "/exports",
    enabled: true,
  },
];

export function Sidebar() {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = (item: NavItem) => {
    if (!item.enabled) return;
    navigate(item.path);
  };

  const isActive = (item: NavItem) => {
    if (item.key === "map") {
      // Map is the index route + the import wizard nests under it.
      return (
        location.pathname === "/" || location.pathname.startsWith("/assets/import")
      );
    }
    if (item.key === "workers") return location.pathname.startsWith("/workers");
    if (item.key === "queue-health") return location.pathname.startsWith("/admin/queue-health");
    if (item.key === "clock-skew") return location.pathname.startsWith("/admin/clock-skew");
    // Phase 7 Plan 03 (D-164) — PM admin Outdated APK tile.
    if (item.key === "outdated-apk") return location.pathname.startsWith("/admin/outdated-apk");
    // Phase 6 Plan 02 (D-138) — /settings cadence pane.
    if (item.key === "settings") return location.pathname.startsWith("/settings");
    // Phase 7 Plan 03 (D-150) — /exports page.
    if (item.key === "exports") return location.pathname.startsWith("/exports");
    return false;
  };

  return (
    <aside className="bg-surface w-60 self-stretch pbs-4 pbe-4" data-testid="sidebar">
      {NAV_ITEMS.map((item) => {
        const label = intl.formatMessage({ id: item.labelId });
        const coming = item.comingLabelId
          ? intl.formatMessage({ id: item.comingLabelId })
          : undefined;
        const active = isActive(item);

        if (item.enabled) {
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => handleClick(item)}
              data-testid={`nav-${item.key}`}
              className={`w-full h-11 flex items-center gap-3 text-sm font-semibold text-start ps-6 pe-6 transition-colors duration-200 ${
                active
                  ? "bg-secondary-container border-s-4 border-primary text-on-secondary-container ps-5"
                  : "text-on-surface hover:bg-surface-raised"
              }`}
            >
              {label}
            </button>
          );
        }

        return (
          <div
            key={item.key}
            data-testid={`nav-${item.key}`}
            title={coming}
            className="w-full h-11 flex items-center justify-between gap-3 text-sm font-semibold ps-6 pe-6 text-text-secondary cursor-not-allowed"
            aria-disabled="true"
          >
            <span>{label}</span>
            <Info size={14} className="shrink-0" />
          </div>
        );
      })}
    </aside>
  );
}
